<script setup lang="ts">
import { ref, nextTick, watch } from 'vue'

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  removable: {
    type: Boolean,
    default: true
  },
  allowEdit: {
    type: Boolean,
    default: true
  },
  showActions: {
    type: Boolean,
    default: false
  },
  chipId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['remove', 'update:label', 'edit', 'toggle-actions'])

const isEditing = ref(false)
const editValue = ref(props.label)
const inputRef = ref<HTMLInputElement>()

watch(() => props.label, (newLabel) => {
  editValue.value = newLabel
})

const toggleActions = (event: Event) => {
  event.stopPropagation()
  if (isEditing.value) return
  emit('toggle-actions', props.chipId)
}

const startEdit = async () => {
  if (!props.allowEdit) return
  
  emit('toggle-actions', null) // Close popup
  isEditing.value = true
  editValue.value = props.label
  await nextTick()
  inputRef.value?.focus()
  inputRef.value?.select()
}

const confirmEdit = () => {
  if (editValue.value.trim() !== '' && editValue.value !== props.label) {
    emit('update:label', editValue.value.trim())
  }
  isEditing.value = false
}

const cancelEdit = () => {
  editValue.value = props.label
  isEditing.value = false
}

const handleBlur = () => {
  // Small delay to allow for potential click events
  setTimeout(() => {
    if (isEditing.value) {
      confirmEdit()
    }
  }, 150)
}

const handleRemove = () => {
  emit('toggle-actions', null) // Close popup
  emit('remove')
}
</script>

<template>
  <div 
    ref="chipRef"
    class="chip-container" 
    :class="{ 'editing': isEditing }"
    v-bind="$attrs"
  >
    <div 
      class="editable-chip" 
      :class="{ 'active': props.showActions }"
      @click="toggleActions"
    >
      <input
        v-if="isEditing"
        ref="inputRef"
        v-model="editValue"
        class="chip-input"
        @blur="handleBlur"
        @keydown.enter="confirmEdit"
        @keydown.escape="cancelEdit"
        @click.stop
        data-cy="chip-edit-input"
      />
      <span v-else class="chip-label" data-cy="chip-label">{{ label }}</span>
    </div>
    
    <!-- Floating Action Popup -->
    <Transition name="action-popup">
      <div v-if="props.showActions && !isEditing" class="action-popup">
        <div class="action-buttons">
          <button 
            class="action-btn edit-btn"
            @click.stop="startEdit"
            :disabled="!allowEdit"
            title="Edit department"
            data-cy="chip-edit-action"
          >
            <i class="pi pi-pencil"></i>
          </button>
          <button 
            v-if="removable"
            class="action-btn delete-btn"
            @click.stop="handleRemove"
            title="Delete department"
            data-cy="chip-delete-action"
          >
            <i class="pi pi-trash"></i>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.chip-container {
  position: relative;
  display: inline-block;
  margin: 0.25rem;
}

.editable-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 16px;
  font-size: 14px;
  background-color: var(--p-secondary-500);
  color: white;
  padding: 6px 16px;
  min-height: 32px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.editable-chip:hover {
  background-color: var(--p-secondary-600);
}

.editable-chip.active {
  background-color: var(--primary-color);
}

.chip-container.editing .editable-chip {
  background-color: white;
  color: var(--p-secondary-500);
  border: 1px solid var(--primary-color);
}

.chip-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.chip-input {
  border: none;
  outline: none;
  background: transparent;
  color: inherit;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  min-width: 60px;
  max-width: 200px;
  width: 100%;
}

/* Action Popup */
.action-popup {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  z-index: 1000;
}

.action-buttons {
  display: flex;
  background: white;
  border-radius: 8px;
  padding: 4px;
  gap: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: transparent;
  transition: background-color 0.2s ease;
}

.edit-btn {
  color: var(--p-secondary-500);
}

.edit-btn:hover {
  background-color: rgba(14, 55, 114, 0.1);
}

.delete-btn {
  color: var(--primary-color);
}

.delete-btn:hover {
  background-color: rgba(255, 135, 0, 0.1);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Simple fade transition */
.action-popup-enter-active,
.action-popup-leave-active {
  transition: opacity 0.2s ease;
}

.action-popup-enter-from,
.action-popup-leave-to {
  opacity: 0;
}
</style>